import Button from '@mui/material/Button'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import { History } from 'history'
import React from 'react'

import Filters from '@/app/component/atom/filters'
import FilterTags from '@/app/component/atom/filters/tags'
import { FilterOption } from '@/app/component/atom/filters/types'
import SearchBar from '@/app/component/atom/search'
import TabHeader from '@/app/component/atom/tab-header'
import CustomTooltip from '@/app/component/atom/tooltip'

import { LogNavigatePayload } from '../helpers'

type Props = {
  filters: Record<string, FilterOption>
  history: History
  loading: boolean
  search: string
  tab: 'messages' | 'calls' | 'whatsapp' | 'topups'
  download: () => void
  navigateLogs: (payload: LogNavigatePayload) => void
  reloadData: () => void
  setSearch: (search: string) => void
}

const LogToolbar: React.FC<Props> = ({
  filters,
  history,
  loading,
  search,
  tab,
  download,
  navigateLogs,
  reloadData,
  setSearch,
}) => {
  return (
    <TabHeader
      action={
        <Toolbar>
          <FilterTagsWrapper>
            <FilterTags
              filters={filters}
              onChange={({ name, value }) => {
                reloadData()
                navigateLogs({
                  search,
                  history,
                  tab,
                  filters: {
                    ...filters,
                    [name]: {
                      ...filters[name],
                      value,
                    },
                  },
                })
              }}
            />
          </FilterTagsWrapper>
          <div>
            <SearchBar
              label={searchLabelMap[tab]}
              namespace="logs"
              tooltipTitle={searchTooltipMap[tab]}
              disabled={loading}
              searchTerm={search}
              onSubmit={(searchTerm: string) => {
                setSearch(searchTerm)
                navigateLogs({
                  tab,
                  history,
                  search: searchTerm,
                  filters,
                })
              }}
            />
          </div>
          <FiltersWrapper id={`${tab}-filter`}>
            <Filters
              data={filters}
              onChange={(key, { value }) => {
                navigateLogs({
                  search,
                  history,
                  tab,
                  filters: {
                    ...filters,
                    [key]: {
                      ...filters[key],
                      value,
                    } as FilterOption,
                  },
                })
              }}
            />
          </FiltersWrapper>
          <DownloadWrapper>
            <CustomTooltip title={`Download the ${tab} list.`}>
              <Button
                className={`download-${tab}-btn`}
                variant="contained"
                onClick={download}
                startIcon={<Icon>cloud_download</Icon>}
              >
                Download {downloadLabels[tab]}
              </Button>
            </CustomTooltip>
          </DownloadWrapper>
          <div>
            <CustomTooltip title={`Reload ${downloadLabels[tab]}`}>
              <IconButton onClick={reloadData}>
                <Icon>refresh</Icon>
              </IconButton>
            </CustomTooltip>
          </div>
        </Toolbar>
      }
      title={tabLabels[tab]}
    />
  )
}

const tabLabels = {
  whatsapp: 'WhatsApp Messages',
  messages: 'SMS Messages',
  calls: 'Calls',
  topups: 'Top-ups',
}

const downloadLabels: { [key: string]: string } = {
  whatsapp: 'WhatsApp messages',
  messages: 'messages',
  calls: 'calls',
  topups: 'top-ups',
}

const searchTooltipMap: { [key: string]: string } = {
  messages: 'Search messages & phone numbers',
  calls: 'Search by phone number prefixes',
  whatsapp: 'Search messages & phone numbers',
  topups: 'Search top-ups & phone numbers',
}

const searchLabelMap: { [key: string]: string } = {
  messages: 'Search',
  calls: 'Phone number prefixes',
  whatsapp: 'Search',
  topups: 'Search',
}

const FilterTagsWrapper = styled('div')({
  flex: '1 auto',
})

const FiltersWrapper = styled('div')({
  flex: '0',
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-end',
})

const DownloadWrapper = styled('div')({
  minWidth: 'fit-content',
})

const Toolbar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

export default LogToolbar
